import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { useCheckpointAuthStore } from '../stores/checkpointAuthStore';
import LoadingSpinner from '../components/LoadingSpinner';
import CheckpointSelector from '../components/CheckpointSelector';
import AppLayout from '../components/AppLayout';
import CheckpointInterface from '../components/CheckpointInterface';

/**
 * Página de Checkpoint (Refatorada)
 * Gerencia o carregamento de dados e a comunicação com o backend,
 * utilizando o component CheckpointInterface para a UI.
 */
function CheckpointPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const userCheckpoint = useCheckpointAuthStore(state => state.user);
    const userDashboard = useAuthStore(state => state.user);
    const isCheckpointContext = !!document.getElementById('haklai-checkpoint');
    const user = isCheckpointContext ? userCheckpoint : userDashboard;

    const cellId = searchParams.get('cell_id');
    const eventId = searchParams.get('event_id');

    // ESTADOS DE CARREGAMENTO
    const { data: cellsData } = useQuery({
        queryKey: ['cells'],
        queryFn: async () => {
            const url = isCheckpointContext ? '/cells?only_led_by_me=true' : '/cells';
            const response = await api.get(url);
            return response.data.data || [];
        },
    });

    const { data: eventsData } = useQuery({
        queryKey: ['events'],
        queryFn: async () => {
            const response = await api.get('/events');
            const data = response.data.data;
            return data?.items || (Array.isArray(data) ? data : []);
        },
    });

    const { data: membersData, isLoading: membersLoading } = useQuery({
        queryKey: ['cell-members', cellId],
        queryFn: async () => {
            if (!cellId) return [];
            const response = await api.get(`/members?cell_id=${cellId}`);
            return response.data.data || [];
        },
        enabled: !!cellId,
    });

    const { data: existingCheckpoint } = useQuery({
        queryKey: ['checkpoint', cellId, eventId],
        queryFn: async () => {
            if (!cellId || !eventId) return null;
            try {
                const response = await api.get(`/checkpoints?cell_id=${cellId}&event_id=${eventId}`);
                const checkpoints = response.data.data?.items || [];
                return checkpoints.length > 0 ? checkpoints[0] : null;
            } catch {
                return null;
            }
        },
        enabled: !!cellId && !!eventId,
    });

    // MUTATION: Salvar Checkpoint
    const checkpointMutation = useMutation({
        mutationFn: async (report) => {
            let targetEventId = eventId;
            
            if (!targetEventId && report.eventData) {
                // Mapear os tipos selecionados na interface para os chaves do backend
                let mappedEventType = 'outro';
                if (report.eventData.type === 'Reunião de Célula') mappedEventType = 'celula';
                if (report.eventData.type === 'Reunião de Líderes') mappedEventType = 'reuniao_lideres';

                const eventPayload = {
                    title: report.eventData.type === 'outro' ? 'Evento Extraordinário' : report.eventData.type,
                    event_type: mappedEventType,
                    event_date: report.eventData.date,
                    start_time: report.eventData.time,
                    description: report.eventData.description,
                    cell_id: report.cell_id,
                    status: 'scheduled'
                };
                const eventResponse = await api.post('/events', eventPayload);
                targetEventId = eventResponse.data.data.id;
            }

            // Se já existe um checkpoint, atualizamos. Caso contrário, criamos.
            if (existingCheckpoint) {
                // Atualiza o checkpoint principal
                await api.put(`/checkpoints/${existingCheckpoint.id}`, {
                    notes: report.notes,
                    total_visitors: report.visitors.length
                });

                // Registra/Atualiza as presenças
                return api.post(`/checkpoints/${existingCheckpoint.id}/attendances`, {
                    attendances: report.attendances,
                    visitors: report.visitors
                });
            } else {
                // Cria novo checkpoint
                const createResponse = await api.post('/checkpoints', {
                    event_id: targetEventId,
                    cell_id: report.cell_id,
                    notes: report.notes,
                    member_id: user.member_id // Quem está realizando
                });

                const newCheckpoint = createResponse.data.data;

                // Registra as presenças no novo checkpoint
                return api.post(`/checkpoints/${newCheckpoint.id}/attendances`, {
                    attendances: report.attendances,
                    visitors: report.visitors
                });
            }
        },
        onSuccess: () => {
            localStorage.removeItem(`checkpoint-draft-${cellId}-${eventId}`);
            queryClient.invalidateQueries({ queryKey: ['checkpoint', cellId, eventId] });
            // Redireciona para relatórios conforme solicitado
            setTimeout(() => {
                navigate('/reports');
            }, 3000); // Aguarda a animação de sucesso do componente interface
        },
        onError: (error) => {
            alert('Erro ao enviar checkpoint: ' + (error.response?.data?.message || error.message));
        },
    });

    // HANDLERS
    const handleSubmit = (report, onSuccessAnimation) => {
        checkpointMutation.mutate(report, {
            onSuccess: () => {
                onSuccessAnimation();
            }
        });
    };

    const handleBack = () => {
        if (isCheckpointContext) {
            navigate('/select');
        } else {
            navigate('/dashboard');
        }
    };

    // RENDERIZAÇÃO
    if (!cellId) {
        return <CheckpointSelector />;
    }

    if (membersLoading) {
        return (
            <AppLayout>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
                    <LoadingSpinner size="lg" />
                </div>
            </AppLayout>
        );
    }

    const selectedCell = cellsData?.find(c => c.id === parseInt(cellId));
    const selectedEvent = eventId ? eventsData?.find(e => e.id === parseInt(eventId)) : null;

    // Draft local se existir
    const draft = JSON.parse(localStorage.getItem(`checkpoint-draft-${cellId}-${eventId}`) || 'null');

    return (
        <AppLayout hideLayout={true}> {/* Escondemos o layout padrão para usar a interface premium mobile-first */}
            <CheckpointInterface 
                cell={selectedCell}
                event={selectedEvent}
                members={membersData}
                initialData={existingCheckpoint || draft}
                loading={checkpointMutation.isPending}
                onSubmit={handleSubmit}
                onBack={handleBack}
            />
        </AppLayout>
    );
}

export default CheckpointPage;
